import type { ICellRendererParams } from "ag-grid-community";
import type { ReactNode } from "react";
import "./key-value-table.css";

import { rowValueType } from "./utils";
import type { KeyValueRow } from "./utils";

import { SimpleTable } from "@/components/ui/ag-grid-table";
import type { SimpleTableColumn } from "@/components/ui/ag-grid-table";

const ValueCell = ({ value }: { value: unknown }) => {
  const type = rowValueType(value);
  if (type === "string") {
    return <span className="font-mono text-xs text-green-700">&quot;{String(value)}&quot;</span>;
  }
  if (type === "number" || type === "boolean") {
    return <span className="font-mono text-xs text-blue-600">{String(value)}</span>;
  }
  if (type === "null") {
    return <span className="font-mono text-xs text-gray-400">null</span>;
  }
  if (type === "array") {
    return <span className="font-mono text-xs text-gray-700">{JSON.stringify(value)}</span>;
  }
  const keyCount = Object.keys(value as object).length;
  return (
    <span className="text-xs text-gray-500">
      {keyCount} {keyCount === 1 ? "key" : "keys"}
    </span>
  );
};

const KeyInnerRenderer = ({
  data,
  variableChips,
}: ICellRendererParams<KeyValueRow> & { variableChips?: boolean }) => {
  const row = data;
  if (!row) {
    return null;
  }
  if (row.key && variableChips && row.depth === 0) {
    return (
      <span className="rounded bg-blue-50 px-1.5 py-0.5 font-mono text-xs text-blue-700">
        {"{{"}
        {row.key}
        {"}}"}
      </span>
    );
  }
  return (
    <span className={row.key ? "font-mono text-xs text-gray-800" : "text-xs italic text-gray-400"}>
      {row.key || "value"}
    </span>
  );
};

const TypeCellRenderer = ({ data }: ICellRendererParams<KeyValueRow>) => (
  <span className="text-xs text-gray-400">{data ? rowValueType(data.value) : ""}</span>
);

const ValueCellRenderer = ({ data }: ICellRendererParams<KeyValueRow>) =>
  data ? (
    <div className="w-full min-w-0 overflow-x-auto whitespace-nowrap">
      <ValueCell value={data.value} />
    </div>
  ) : null;

const KeyValueTable = ({
  rows,
  variableChips = false,
  renderActions,
}: {
  rows: KeyValueRow[];
  variableChips?: boolean;
  renderActions?: (row: KeyValueRow) => ReactNode;
}) => {
  const columns: SimpleTableColumn<KeyValueRow>[] = [
    {
      header: "Type",
      field: "value",
      width: 90,
      cellRenderer: TypeCellRenderer,
    },
    {
      header: "Value",
      field: "value",
      flex: 1,
      cellRenderer: ValueCellRenderer,
    },
    ...(renderActions
      ? [
          {
            header: "Actions",
            field: "id",
            width: 90,
            cellRenderer: ({ data }: ICellRendererParams<KeyValueRow>) =>
              data ? renderActions(data) : null,
          } as SimpleTableColumn<KeyValueRow>,
        ]
      : []),
  ];

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200">
      <SimpleTable<KeyValueRow>
        data={rows}
        columns={columns}
        enablePagination={false}
        enableSorting={false}
        enableFiltering={false}
        suppressHeaderMenuButton
        rowHeight={30}
        treeData
        getDataPath={(row: KeyValueRow) => row.path}
        getRowId={(params: { data: KeyValueRow }): string => params.data.id}
        autoGroupColumnDef={{
          headerName: "Key",
          suppressHeaderMenuButton: true,
          width: 220,
          cellRendererParams: {
            suppressCount: true,
            innerRenderer: KeyInnerRenderer,
            variableChips,
          },
        }}
        gridOptions={{ groupDefaultExpanded: -1, domLayout: "autoHeight", headerHeight: 32 }}
        className="pl-key-value-table [&_.ag-root-wrapper]:border-none"
      />
    </div>
  );
};

export default KeyValueTable;
